// 📄 interfazZService.ts
// Catálogo de interfaces/canales de integración personalizados (desarrollos
// Z) por proyecto — mismo patrón de acceso que transaccionZService.ts /
// badiZService.ts: Firestore DIRECTO desde el cliente (colección
// projects/{projectId}/interfacesZ), protegido por firestore.rules (mismo
// criterio exacto que transaccionesZ).
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase";
import {
  ObjetoNegocio,
  SolucionInterfaz,
  PlataformaInterfaz,
  DireccionInterfaz
} from "./catalogoInterfacesService.ts";
import { SistemaAmbiente } from "./transaccionZService.ts";

export interface InterfazZData {
  objetoNegocio: ObjetoNegocio | "";
  solucion: SolucionInterfaz;
  plataforma: PlataformaInterfaz;
  transaccion: string;
  direccion: DireccionInterfaz;
  textoBreve: string;
  nombreTecnico: string;
  campoBusqueda: string;
  tecnologia: string;
  middleware: string;
  palabrasClave: string[];
  descripcion: string;
  responsable: string;
  sistemasDisponibles: SistemaAmbiente[];
  fechaImplementacion: Timestamp | null;
  notas: string;
}

export interface InterfazZ extends InterfazZData {
  id: string;
  creadoPor: string;
  creadoEn: any;
  actualizadoEn: any;
}

const interfacesZRef = (projectId: string) => collection(db, "projects", projectId, "interfacesZ");
const interfazZRef = (projectId: string, id: string) => doc(db, "projects", projectId, "interfacesZ", id);

export const getInterfacesZ = async (projectId: string): Promise<InterfazZ[]> => {
  const snap = await getDocs(interfacesZRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<InterfazZ, "id">) }))
    .sort((a, b) => a.nombreTecnico.localeCompare(b.nombreTecnico));
};

const validar = (data: InterfazZData) => {
  if (!data.objetoNegocio) throw new Error("El objeto de negocio es obligatorio.");
  if (!data.solucion) throw new Error("La solución es obligatoria.");
  if (!data.plataforma) throw new Error("La plataforma es obligatoria.");
  if (!data.direccion) throw new Error("La dirección es obligatoria.");
  if (!data.textoBreve.trim()) throw new Error("El texto breve es obligatorio.");
  if (!data.nombreTecnico.trim()) throw new Error("El nombre técnico / valor a buscar es obligatorio.");
};

export const crearInterfazZ = async (projectId: string, data: InterfazZData, creadoPor: string): Promise<string> => {
  validar(data);

  const ref = doc(interfacesZRef(projectId));
  await setDoc(ref, {
    ...data,
    transaccion: data.transaccion.trim(),
    textoBreve: data.textoBreve.trim(),
    nombreTecnico: data.nombreTecnico.trim(),
    creadoPor,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const actualizarInterfazZ = async (projectId: string, id: string, data: InterfazZData): Promise<void> => {
  validar(data);

  await updateDoc(interfazZRef(projectId, id), {
    ...data,
    transaccion: data.transaccion.trim(),
    textoBreve: data.textoBreve.trim(),
    nombreTecnico: data.nombreTecnico.trim(),
    actualizadoEn: serverTimestamp()
  });
};

export const eliminarInterfazZ = async (projectId: string, id: string): Promise<void> => {
  await deleteDoc(interfazZRef(projectId, id));
};
